/**
 * Filter Persistence Service - Manages filter state across sessions
 * Provides functionality for saving and restoring search filters
 */

const FILTER_STATE_KEY = 'recrutees_filter_state';
const FILTER_PRESETS_KEY = 'recrutees_filter_presets';

/**
 * Saves current filter state
 * @param {Object} filters - Current filter values
 */
export function saveFilterState(filters) {
  try {
    if (!filters || typeof filters !== 'object') return;

    const filterState = {
      ...filters,
      timestamp: new Date()?.toISOString()
    };

    localStorage.setItem(FILTER_STATE_KEY, JSON.stringify(filterState));
  } catch (error) {
    console.error('Error saving filter state:', error);
  }
}

/**
 * Loads saved filter state
 * @returns {Object|null} Saved filter state or null
 */
export function loadFilterState() {
  try {
    const stored = localStorage.getItem(FILTER_STATE_KEY);
    if (!stored) return null;

    const filterState = JSON.parse(stored);
    
    // Check if filters are not too old (e.g., 7 days)
    const timestamp = new Date(filterState.timestamp);
    const daysSinceLastUse = (Date.now() - timestamp?.getTime()) / (1000 * 60 * 60 * 24);
    
    if (daysSinceLastUse > 7) {
      clearFilterState();
      return null;
    }

    // Remove timestamp before returning
    const { timestamp: _, ...filters } = filterState;
    return filters;
  } catch (error) {
    console.error('Error loading filter state:', error);
    return null;
  }
}

/**
 * Clears saved filter state
 */
export function clearFilterState() {
  try {
    localStorage.removeItem(FILTER_STATE_KEY);
  } catch (error) {
    console.error('Error clearing filter state:', error);
  }
}

/**
 * Saves a filter preset
 * @param {string} name - Preset name
 * @param {Object} filters - Filter values
 * @returns {Object} Saved preset
 */
export function saveFilterPreset(name, filters) {
  try {
    if (!name?.trim() || !filters) {
      throw new Error('Name and filters are required');
    }

    const presets = getFilterPresets();
    
    const preset = {
      id: Date.now()?.toString(),
      name: name?.trim(),
      filters,
      createdAt: new Date()?.toISOString()
    };

    // Check for duplicate names
    const existingIndex = presets?.findIndex(p => 
      p?.name?.toLowerCase() === name?.trim()?.toLowerCase()
    );

    if (existingIndex >= 0) {
      // Update existing preset
      presets[existingIndex] = preset;
    } else {
      // Add new preset
      presets?.unshift(preset);
    }

    localStorage.setItem(FILTER_PRESETS_KEY, JSON.stringify(presets));
    return preset;
  } catch (error) {
    console.error('Error saving filter preset:', error);
    throw error;
  }
}

/**
 * Gets all filter presets
 * @returns {Array} Filter presets
 */
export function getFilterPresets() {
  try {
    const stored = localStorage.getItem(FILTER_PRESETS_KEY);
    if (!stored) return [];

    const presets = JSON.parse(stored);
    return Array.isArray(presets) ? presets : [];
  } catch (error) {
    console.error('Error getting filter presets:', error);
    return [];
  }
}

/**
 * Deletes a filter preset
 * @param {string} id - Preset ID
 */
export function deleteFilterPreset(id) {
  try {
    const presets = getFilterPresets();
    const filtered = presets?.filter(preset => preset?.id !== id);
    localStorage.setItem(FILTER_PRESETS_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error('Error deleting filter preset:', error);
  }
}

/**
 * Applies a filter preset
 * @param {string} id - Preset ID
 * @returns {Object|null} Filter values or null
 */
export function applyFilterPreset(id) {
  try {
    const presets = getFilterPresets();
    const preset = presets?.find(p => p?.id === id);
    
    if (preset) {
      saveFilterState(preset?.filters);
      return preset?.filters;
    }

    return null;
  } catch (error) {
    console.error('Error applying filter preset:', error);
    return null;
  }
}

/**
 * Gets default filter state
 * @returns {Object} Default filters
 */
export function getDefaultFilters() {
  return {
    query: '',
    location: { city: '' },
    workMode: 'ANY',
    datePosted: 'ANY',
    jobTypes: [],
    experienceLevel: [],
    industries: [],
    companySize: [],
    skills: [],
    languages: [],
    benefits: [],
    salaryRange: { min: '', max: '', currency: 'USD' }
  };
}